import { useCallback, useEffect, useMemo, useState } from "react";

import isEmpty from "lodash/isEmpty";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  GroupClassSeriesAnalyticsEvent,
  GroupClassSeriesAnalyticsSources,
  TabAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import {
  ExploreAnalyticsEvent,
  ExploreAnalyticsSources,
} from "@/constants/analytics/Explore";
import { TabSources } from "@/constants/analytics/Tabs";
import { ExploreTabSubDeepLinks } from "@/constants/Explore";
import { ModalPath } from "@/constants/Navigation";
import { GroupClassCarouselVariantValues } from "@/constants/Schedule";
import { FABTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useSearchGroupsSubDeepLink from "@/hooks/deepLinking/subDeepLinks/useSearchGroupsSubDeepLink";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import useEncodedSearchParams from "@/hooks/useEncodedSearchParams";
import usePatientStatus from "@/hooks/usePatientStatus";
import { useAnalytics } from "@/services/Analytics";
import {
  useGetFullProfileQuery,
  useLazyGetContentFeedQuery,
} from "@/store/api/user";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { ExploreContentHeader } from "@/ui/components/explore/ExploreContentHeader";
import ExploreResourcesCarousel from "@/ui/components/explore/ExploreResourcesCarousel";
import GroupClassSeriesCarousel from "@/ui/components/explore/GroupClassSeriesCarousel";
import WhyAttendGroupsAccordion from "@/ui/components/explore/WhyAttendGroupsAccordion";
import { FAIconName, FloatingButton } from "@/ui/core";
import {
  ScreenContainer,
  ScrollView,
  VStack,
  WebContentConstraints,
} from "@/ui/layout";
import { ListDimensions } from "@/ui/layout/list/ListComponents";
import { ColorToken } from "@/ui/theme/tokens";
import { encodeNavigationParams } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";
import { shuffleContentThemeResources } from "@/utils/resource.util";

const useVideoContentSubDeepLink = () => {
  const {
    componentSource,
    deepLinkUrl,
    title,
    url: videoUrl,
  } = useEncodedSearchParams<{
    componentSource?: string;
    deepLinkUrl?: string;
    title?: string;
    url?: string;
  }>();

  const handleOpenVideoContent = useCallback(() => {
    router.navigate({
      params: encodeNavigationParams({
        componentSource,
        deepLinkUrl,
        title,
        url: videoUrl,
      }),
      pathname: ModalPath.VideoContent,
    });
  }, [componentSource, deepLinkUrl, title, videoUrl]);

  useSubDeepLink({
    analyticsEvent: ExploreAnalyticsEvent.VideoContentOpenedFromDeepLink,
    analyticsProps: {
      component: AnalyticsComponent.Tab,
      source: ExploreAnalyticsSources.ExploreScreen,
    },
    onOpen: handleOpenVideoContent,
    targetValue: ExploreTabSubDeepLinks.VideoContent,
  });
};

const Tab = () => {
  const { externalId: appUserId, isPatient: isAppUserPatient } =
    useAppUserSelector();

  const { track, trackPageView } = useAnalytics();

  const { data: fullProfileData } = useGetFullProfileQuery();
  const { isCurrentOrSupportLinkedUserInTreatment } =
    usePatientStatus(fullProfileData);

  const handleSearchGroups = useCallback(() => {
    track(GroupClassSeriesAnalyticsEvent.SearchGroupsPressed, {
      [AnalyticsFieldName.Component]: FloatingButton.name,
      [AnalyticsFieldName.Source]: TabSources.Explore,
    });
    router.navigate(ModalPath.GroupClassList);
  }, [track]);

  useSearchGroupsSubDeepLink({
    onOpen: handleSearchGroups,
  });
  useVideoContentSubDeepLink();

  useEffect(() => {
    trackPageView(TabAnalyticsPageView.Explore);
  }, [trackPageView]);

  const [contentFeed, setContentFeed] = useState<ResourceTheme[]>([]);
  const [getContentFeed] = useLazyGetContentFeedQuery();
  useEffect(() => {
    void getContentFeed(
      {
        isPatient: isAppUserPatient,
        isPromoted: false,
        userExternalId: appUserId,
      },
      // true is for preferCacheValue argument, to return cached value if it exists (by default set to false)
      true,
    )
      .unwrap()
      .then((themes: ResourceTheme[]) =>
        setContentFeed(themes.map(shuffleContentThemeResources)),
      );
  }, [appUserId, isAppUserPatient, getContentFeed]);
  const apiProvidedCarousels = useMemo(() => {
    if (isEmpty(contentFeed)) return null;
    return contentFeed.map((theme: ResourceTheme) => (
      <ExploreResourcesCarousel
        data={theme.resources}
        key={theme.name}
        title={theme.name}
      />
    ));
  }, [contentFeed]);

  return (
    <ScreenContainer
      backgroundColor={isWeb() ? undefined : ColorToken.Secondary5}
    >
      <ScrollView
        backgroundColor={ColorToken.NeutralBackgroundPrimary}
        bounces={false}
        // on web, maxWidth+center content; ScrollView stays full-width so scroll works viewport-wide.
        contentContainerStyle={{
          ...WebContentConstraints,
          paddingBottom: ListDimensions.FABBottomOffset,
        }}
      >
        <ExploreContentHeader />
        <VStack backgroundColor={ColorToken.NeutralBackgroundPrimary}>
          <GroupClassSeriesCarousel
            analyticsSource={GroupClassSeriesAnalyticsSources.YourGroups}
            tabSource="explore"
            variant={GroupClassCarouselVariantValues.yourGroups}
          />

          {isCurrentOrSupportLinkedUserInTreatment && (
            <GroupClassSeriesCarousel
              analyticsSource={
                GroupClassSeriesAnalyticsSources.ExploreTabGroups
              }
              tabSource="explore"
              variant={GroupClassCarouselVariantValues.basicGroups}
            />
          )}

          {isCurrentOrSupportLinkedUserInTreatment && (
            <WhyAttendGroupsAccordion />
          )}

          {apiProvidedCarousels}
        </VStack>
      </ScrollView>
      {isCurrentOrSupportLinkedUserInTreatment && (
        <FloatingButton
          leftIcon={FAIconName.Search}
          onPress={handleSearchGroups}
          testID={FABTestID.SearchGroups}
          title="Search groups"
        />
      )}
    </ScreenContainer>
  );
};

export default Tab;
